import * as z from 'zod';

import { countryCodes } from './country-database.js';

/** I'm not enumerating countries - SORRY. We have to rely just on validation */
export type Country = {
  name: string;
  code: string;
};

/** @NOTE we do not validate country name (it has to match country code)  */
export const ZodCountry = z.object({
  name: z.string(),
  code: z.enum(countryCodes),
});

export type Address = {
  addressLine: string;
  city: string;
  state: string;
  postalCode: string;
  country: Country;
};

export const ZodAddress = z.object({
  addressLine: z.string().min(1),
  city: z.string().min(1),
  state: z.string().min(1),
  postalCode: z.string().min(1),
  country: ZodCountry,
});

export enum AssuranceLevel {
  VAL = 'VAL',
  REV = 'REV',
  VER = 'VER',
  UR = 'UR',
}

export const ZodAssuranceLevel = z.enum(AssuranceLevel);

export enum SustainabilityTier {
  A = 'A',
  B = 'B',
  C = 'C',
}

export const ZodSustainabilityTier = z.enum(SustainabilityTier);

/**
 * This enum contains human-readable values for each regulatory scheme (instead of typical `franceBlendingMandate` or something).
 * This was original implementation. However, names has been changed, so I created
 * RegulatorySchemeToLabel. New regulatory schemes have been added that follow the same human-readable convention.
 * @TODO https://github.com/energywebfoundation/safc-registry/issues/2605
 */
export enum RegulatoryScheme {
  France = 'France Blending Mandate *',
  Norway = 'Norway Blending Mandate *',
  RefuelEU = 'Refuel EU *',
  Netherlands = 'Netherlands HBE *',
  Brazil = 'Brazil Fuel of the Future Program *',
  Canadian = 'Canadian Clean Fuel Regulations *',
  SouthKorea = 'South Korea SAF Mandate *',
  UK = 'UK SAF Mandate *',

  California = 'California LCFS',
  Washington = 'Washington State Clean Fuels Standard',
  Oregon = 'Oregon Clean Fuels Program',
  BritishColumbia = 'British Columbia LCFS',
  UsIRA = 'US IRA tax credits',
  UsRFS = 'US RFS RINs',
  UKRTFO = 'UK Renewable Fuels Transport Obligation (RTFO)',
  Colorado = 'Colorado Tax Credit for SAF Production Facility',
  Minnesota = 'Minnesota SAF Tax Credit',
  Nebraska = 'Nebraska SAF Tax Credit',
  Singapore = 'Singapore SAF Levy',
  UKRCM = 'UK Revenue Certainty Mechanism (RCM)',
  WashingtonTaxCredit = 'Washington State SAF Tax Credit (ESSB 5447)',
  None = 'None',
}

export const RegulatorySchemeToLabel: Record<RegulatoryScheme, string> = {
  [RegulatoryScheme.France]: 'France SAF Blending Mandate *',
  [RegulatoryScheme.Norway]: 'Norway SAF Blending Mandate *',
  [RegulatoryScheme.Netherlands]: 'Netherlands HBE *',
  [RegulatoryScheme.RefuelEU]: 'Refuel EU *',
  [RegulatoryScheme.UK]: 'UK SAF Mandate *',
  [RegulatoryScheme.Brazil]: 'Brazil Fuel of the Future Program *',
  [RegulatoryScheme.Canadian]: 'Canadian Clean Fuel Regulations *',
  [RegulatoryScheme.SouthKorea]: 'South Korea SAF Mandate *',

  [RegulatoryScheme.California]: 'California Low Carbon Fuel Standard',
  [RegulatoryScheme.BritishColumbia]: 'British Columbia Low Carbon Fuel Standard - Carbon Intensity Standard +',
  [RegulatoryScheme.Oregon]: 'Oregon Clean Fuels Program',
  [RegulatoryScheme.UKRTFO]: 'UK Renewable Fuels Transport Obligation (RTFO)',
  [RegulatoryScheme.UsIRA]: 'US IRA Tax Credit (§45Q, 45V, 45Z)',
  [RegulatoryScheme.UsRFS]: 'US Renewable Fuel Standards RINs',
  [RegulatoryScheme.Washington]: 'Washington State Clean Fuel Standard',
  [RegulatoryScheme.Colorado]: 'Colorado Tax Credit for SAF Production Facility',
  [RegulatoryScheme.Minnesota]: 'Minnesota SAF Tax Credit',
  [RegulatoryScheme.Nebraska]: 'Nebraska SAF Tax Credit',
  [RegulatoryScheme.Singapore]: 'Singapore SAF Levy',
  [RegulatoryScheme.UKRCM]: 'UK Revenue Certainty Mechanism (RCM)',
  [RegulatoryScheme.WashingtonTaxCredit]: 'Washington State SAF Tax Credit (ESSB 5447)',
  [RegulatoryScheme.None]: 'None',
};

export const ZodRegulatoryScheme = z.enum(RegulatoryScheme);

export enum DocumentationType {
  PoS = 'pos',
  PoC = 'poc',
}

export const ZodDocumentationType = z.enum(DocumentationType);

/**
 * Old json-schema did not have that. It was solely decided on frontend, and then
 * frontend saved additional metadata: adjustedLCAValue and sustainabilityTier.
 * Now, during issuance the lca value is checked, and if it requires adjusting it checks
 * the choice made (part of metadata); and the choice >has to< be made.
 * Then saves updated values in metadata like previously to avoid breaking change.
 */
export enum AdjustedLCAChoice {
  Keep = 'keep', // -> keep negative, convert unit to sustainability tier C
  Adjust = 'adjust', // -> convert to 0, keep unit as current sustainability
}

export const ZodAdjustedLCAChoice = z.enum(AdjustedLCAChoice);

export enum ConversionProcess {
  HEFA = 'hefa',
  ATJIntegrated = 'atjIntegrated',
  ATJStandalone = 'atjStandalone',
  ATJIsobutanol = 'atjIsobutanol',
  FT = 'ft',
  HEFACoProcessed = 'hefaCoProcessed',
  SIP = 'sip',
  PTL = 'ptl',
}

export const ZodConversionProcess = z.enum(ConversionProcess);
